import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { WaypointKind } from '@xingroute/schema'
import {
  IconLock,
  IconPaperclip,
  IconTrash,
  WaypointGlyph,
  waypointGlyphKind,
  waypointGlyphKindForSym,
  type WaypointGlyphKind,
} from './ui/IconButton.js'
import { SYSTEM_KIND_TO_SYM, type SystemWaypointKind } from '../lib/mergedWaypoints.js'

// Unified waypoint row used by both view and edit modes.
// Layout: [icon 24px] [type label w-1/5] [desc flex-1] [attachments 28px] [action 24px]
//
// In view mode: entire row is clickable, desc is plain text.
// In edit mode: entire row is clickable, desc is inline input, icon opens kind picker.
// System rows: always read-only in both modes.

export type WaypointRowMode = 'view' | 'edit'

export interface WaypointRowProps {
  mode: WaypointRowMode
  isSystem: boolean
  typeLabel: string
  desc: string
  glyphKind: WaypointGlyphKind
  selected?: boolean
  attachmentsCount?: number
  // Interactions
  onClick?: () => void
  onDescChange?: (desc: string) => void
  onDelete?: () => void
  onOpenKindPicker?: (anchor: HTMLElement) => void
  onOpenAttachments?: () => void
  // Refs
  rowRef?: (node: HTMLDivElement | null) => void
}

export function WaypointRow({
  mode,
  isSystem,
  typeLabel,
  desc,
  glyphKind,
  selected = false,
  attachmentsCount = 0,
  onClick,
  onDescChange,
  onDelete,
  onOpenKindPicker,
  onOpenAttachments,
  rowRef,
}: WaypointRowProps) {
  const { t } = useTranslation('common')
  const iconRef = useRef<HTMLButtonElement | null>(null)

  const selectedCls = selected
    ? "bg-[var(--xr-brand-primary-soft)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[2px] before:bg-[var(--xr-brand-primary)] before:content-['']"
    : ''

  // Icon cell
  const canChangeKind = mode === 'edit' && !isSystem
  const iconCell = canChangeKind ? (
    <button
      ref={iconRef}
      type="button"
      className="relative inline-flex w-6 flex-none cursor-pointer items-center justify-center rounded text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)]"
      title={t('editor.waypointKind.changeKind')}
      aria-label={t('editor.waypointKind.changeKind')}
      onClick={(e) => {
        e.stopPropagation()
        onOpenKindPicker?.(e.currentTarget)
      }}
    >
      <WaypointGlyph kind={glyphKind} size={16} />
    </button>
  ) : (
    <span className="relative inline-flex w-6 flex-none items-center justify-center text-[var(--xr-text-primary)]">
      <WaypointGlyph kind={glyphKind} size={16} />
    </span>
  )

  // Type label cell
  const typeLabelCls = isSystem
    ? 'text-xs font-medium text-[var(--xr-text-muted)]'
    : 'text-xs font-medium text-[var(--xr-text-primary)]'
  const typeLabelCell = (
    <span className={`w-1/5 flex-none truncate ${typeLabelCls}`} title={typeLabel}>
      {typeLabel}
    </span>
  )

  // Desc cell
  const descCell =
    mode === 'edit' && !isSystem && onDescChange ? (
      <input
        type="text"
        value={desc}
        placeholder={t('editor.waypointList.descriptionPlaceholder')}
        onChange={(e) => onDescChange?.(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="min-w-0 flex-1 truncate border-none bg-transparent px-0 py-0 text-xs leading-[18px] text-[var(--xr-text-muted)] outline-none placeholder:text-[var(--xr-text-muted)]/50 focus:bg-[var(--xr-bg-surface-elev)]"
      />
    ) : (
      <span
        className="min-w-0 flex-1 truncate text-xs text-[var(--xr-text-muted)]"
        title={desc || undefined}
      >
        {desc || (isSystem ? '' : '—')}
      </span>
    )

  // Attachments cell
  const attachmentsCell =
    onOpenAttachments && attachmentsCount > 0 ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onOpenAttachments()
        }}
        className="relative flex w-7 flex-none items-center justify-center rounded py-1 text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] hover:text-[var(--xr-text-primary)]"
        title={t('editor.waypointAttachments.viewTooltip', { count: attachmentsCount })}
      >
        <IconPaperclip size={14} />
        <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--xr-brand-primary)] px-1 text-[10px] font-medium text-white">
          {attachmentsCount}
        </span>
      </button>
    ) : (
      <span className="flex w-7 flex-none items-center justify-center py-1 text-[var(--xr-text-muted)] opacity-30">
        <IconPaperclip size={14} />
      </span>
    )

  // Action cell
  const interactive = Boolean(onClick)
  const actionCell =
    mode === 'edit' && !isSystem && onDelete ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        className="flex w-6 flex-none items-center justify-center rounded py-1 text-[var(--xr-danger)] hover:bg-[var(--xr-danger-soft)]"
        title={t('delete')}
      >
        <IconTrash />
      </button>
    ) : isSystem ? (
      <span className="flex w-6 flex-none items-center justify-center py-1 text-[var(--xr-text-muted)] opacity-50">
        <IconLock size={12} />
      </span>
    ) : (
      <span className="w-6 flex-none" aria-hidden="true" />
    )

  return (
    <li>
      <div
        ref={rowRef}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (!interactive) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}
        className={`relative flex min-h-[38px] w-full items-center gap-2 overflow-hidden py-2 pl-2 pr-1 text-left transition ${interactive ? 'cursor-pointer hover:bg-[var(--xr-bg-surface-elev)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)]' : ''} ${selectedCls}`}
        data-testid="waypoint-row"
        data-system={isSystem || undefined}
        aria-pressed={selected || undefined}
      >
        {iconCell}
        {typeLabelCell}
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {descCell}
          {attachmentsCell}
          {actionCell}
        </div>
      </div>
    </li>
  )
}

// Helpers for deriving row props from data

export function systemTypeLabel(t: (key: string) => string): string {
  return t('editor.waypointList.typeSystem')
}

export function userTypeLabel(
  kind: WaypointKind | undefined,
  name: string | undefined,
  t: (key: string) => string,
): string {
  if (kind && kind !== 'generic') return t(`waypointKind.${kind}`)
  if (name?.trim()) return name.trim()
  return t('waypointKind.generic')
}

export function systemDescLabel(
  systemKind: SystemWaypointKind,
  t: (key: string) => string,
): string {
  return t(`editor.waypointList.system${systemKind.charAt(0).toUpperCase() + systemKind.slice(1)}`)
}

export function resolveGlyphKind(
  isSystem: boolean,
  systemKind: SystemWaypointKind | undefined,
  wpKind: string | undefined,
  wpSym: string | undefined,
  hasAttachments: boolean,
): WaypointGlyphKind {
  if (isSystem && systemKind) {
    return waypointGlyphKindForSym(SYSTEM_KIND_TO_SYM[systemKind])
  }
  return waypointGlyphKind(wpSym, hasAttachments, wpKind)
}
